import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { EquityQuote, ImpliedBeta } from '../../types/finance'
import { getImpliedBeta } from '../../lib/finance-api'
import { FAVORITES_LIST_ID, useWatchlists } from '../../lib/watchlists-context'
import NewsFeed from './NewsFeed'
import PriceChart from './PriceChart'

function formatLargeNumber(n: number | null | undefined): string {
  if (n == null || n === 0) return 'N/A'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  return `$${n.toLocaleString()}`
}

function formatVolume(n: number | null | undefined): string {
  if (n == null) return 'N/A'
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  return n.toLocaleString()
}

// Format an ISO 'YYYY-MM-DD' date without the UTC-parse off-by-one (parse at
// local midnight so the calendar day is preserved across time zones).
function formatReportDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function daysUntilLabel(days: number): string {
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days > 1) return `in ${days} days`
  if (days === -1) return 'yesterday'
  return `${Math.abs(days)} days ago`
}

interface Props {
  quote: EquityQuote
}

export default function QuoteCard({ quote }: Props) {
  const { isFavorited, toggleFavorite } = useWatchlists()
  const favorited = isFavorited(quote.symbol)
  const isPositive = quote.change >= 0

  // Days until the next report: prefer the API's value, otherwise derive it.
  const earningsDays =
    quote.daysUntilEarnings ??
    (quote.nextEarningsDate
      ? Math.round((new Date(`${quote.nextEarningsDate}T00:00:00`).getTime() - Date.now()) / 86400000)
      : null)
  const earningsSoon = earningsDays !== null && earningsDays >= 0 && earningsDays <= 7

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Header with price */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-3">
            {/* Favorite star — saves to the Favorites watchlist */}
            <button
              onClick={() => toggleFavorite(quote.symbol)}
              className={`mt-1 transition-all ${
                favorited
                  ? 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 scale-110'
                  : 'text-slate-400 dark:text-slate-600 hover:text-amber-600 dark:hover:text-amber-400'
              }`}
              title={favorited ? 'Remove from Favorites' : 'Add to Favorites'}
            >
              <svg className="w-6 h-6" fill={favorited ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={favorited ? 0 : 1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{quote.symbol}</h2>
                <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded">{quote.exchange}</span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{quote.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-slate-900 dark:text-white">${quote.currentPrice.toFixed(2)}</p>
            <div className={`flex items-center justify-end gap-1 text-sm font-medium ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              <svg className={`w-4 h-4 ${isPositive ? '' : 'rotate-180'}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span>{isPositive ? '+' : ''}{quote.change.toFixed(2)} ({isPositive ? '+' : ''}{quote.changePercent.toFixed(2)}%)</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <AddToListMenu symbol={quote.symbol} />
          <p className="text-slate-500 text-xs">Last updated: {new Date(quote.lastUpdated).toLocaleString()}</p>
          {quote.nextEarningsDate && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${
                earningsSoon
                  ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/30'
                  : 'bg-slate-200/60 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300'
              }`}
              title={quote.earningsDateEstimated ? 'Estimated reporting window' : 'Confirmed reporting date'}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              <span>Next report: {formatReportDate(quote.nextEarningsDate)}</span>
              {earningsDays !== null && (
                <span className={earningsSoon ? 'text-amber-200/80' : 'text-slate-500 dark:text-slate-400'}>· {daysUntilLabel(earningsDays)}</span>
              )}
              {quote.earningsDateEstimated && <span className="text-slate-500 dark:text-slate-400">(est.)</span>}
            </span>
          )}
        </div>
      </div>

      {/* Price history chart with hover crosshair */}
      <PriceChart symbol={quote.symbol} anchorPrice={quote.currentPrice} anchorPrevClose={quote.previousClose} />

      {/* Metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 dark:bg-slate-700">
        <MetricCell label="Daily Volume" value={formatVolume(quote.dailyVolume)} />
        <MetricCell label="Avg Monthly Volume" value={formatVolume(quote.avgMonthlyVolume)} />
        <MetricCell label="Trailing 12M Beta" value={quote.trailingBeta?.toFixed(2) ?? 'N/A'} highlight={quote.trailingBeta != null && quote.trailingBeta > 1.5} />
        <MetricCell label="Market Cap" value={formatLargeNumber(quote.marketCap)} />
        <MetricCell label="P/E Ratio" value={quote.peRatio?.toFixed(1) ?? 'N/A'} />
        <MetricCell label="Previous Close" value={`$${quote.previousClose.toFixed(2)}`} />
        <MetricCell
          label="Day Range"
          value={quote.dayLow != null && quote.dayHigh != null
            ? `$${quote.dayLow.toFixed(2)} – $${quote.dayHigh.toFixed(2)}`
            : 'N/A'}
        />
        <MetricCell label="Currency" value={quote.currency} />
      </div>

      {/* Implied beta term structure (options-implied, vs SPY) */}
      <ImpliedBetaPanel symbol={quote.symbol} />

      {/* Action links */}
      <div className="flex divide-x divide-slate-200 dark:divide-slate-700">
        <Link
          to={`/dashboard/financial-app/fundamentals/${quote.symbol}`}
          className="flex-1 px-4 py-3 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
        >
          View Fundamentals
        </Link>
        <Link
          to={`/dashboard/financial-app/options/${quote.symbol}`}
          className="flex-1 px-4 py-3 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
        >
          Options Chain
        </Link>
      </div>

      {/* News & Social feed */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        <NewsFeed symbol={quote.symbol} />
      </div>
    </div>
  )
}

/** Dropdown to save/remove this security on any watchlist. */
function AddToListMenu({ symbol }: { symbol: string }) {
  const { lists, isInList, addSymbol, removeSymbol } = useWatchlists()
  const [open, setOpen] = useState(false)
  const memberCount = lists.filter((l) => isInList(l.id, symbol)).length

  return (
    <span className="relative inline-block">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Add to list
        {memberCount > 0 && <span className="text-blue-500/80 dark:text-blue-400/80">· in {memberCount}</span>}
      </button>
      {open && (
        <>
          {/* click-away catcher */}
          <button
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Close list menu"
            tabIndex={-1}
          />
          <div className="absolute z-20 mt-1 left-0 w-56 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl py-1">
            {lists.map((l) => {
              const member = isInList(l.id, symbol)
              return (
                <button
                  key={l.id}
                  onClick={() => (member ? removeSymbol(l.id, symbol) : addSymbol(l.id, symbol))}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="truncate">
                    {l.id === FAVORITES_LIST_ID ? '⭐ ' : ''}
                    {l.name}
                  </span>
                  {member && <span className="text-emerald-500 shrink-0">✓</span>}
                </button>
              )
            })}
            <p className="px-3 pt-1.5 pb-1 text-[10px] text-slate-400 dark:text-slate-500 border-t border-slate-200/70 dark:border-slate-700/70 mt-1">
              Click a list to add or remove. Manage lists from the Watchlists card.
            </p>
          </div>
        </>
      )}
    </span>
  )
}

function MetricCell({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-white dark:bg-slate-800 p-4">
      <p className="text-slate-500 text-xs mb-1">{label}</p>
      <p className={`text-sm font-semibold ${highlight ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>{value}</p>
    </div>
  )
}

const HORIZON_LABELS: Record<string, string> = {
  nearest: 'Nearest',
  '~30d': '~30 day',
  'next-earnings': 'Next report',
}

function ImpliedBetaPanel({ symbol }: { symbol: string }) {
  const [data, setData] = useState<ImpliedBeta | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    getImpliedBeta(symbol)
      .then((d) => { if (active) setData(d) })
      .catch(() => { if (active) setData(null) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [symbol])

  const points = data?.termStructure?.filter((p) => p.impliedBeta != null) ?? []
  const headline = data?.impliedBeta ?? null
  const trailing = data?.trailingBeta ?? null
  const delta = headline != null && trailing != null
    ? (data?.impliedVsTrailing ?? +(headline - trailing).toFixed(2))
    : null

  return (
    <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
      <div className="flex items-baseline gap-2 mb-3">
        <h3 className="text-slate-900 dark:text-white font-semibold text-sm">Implied β</h3>
        <span className="text-slate-500 text-xs">options-implied, vs {data?.market ?? 'SPY'}</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 gap-px bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="bg-white dark:bg-slate-800 h-14" />)}
        </div>
      ) : points.length === 0 ? (
        <p className="text-slate-500 text-xs">{data?.note || 'No options-implied beta available.'}</p>
      ) : (
        <>
          {/* Implied vs trailing comparison */}
          {headline != null && trailing != null && delta != null && (
            <div className="bg-slate-100/50 dark:bg-slate-900/50 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-5">
                <div>
                  <p className="text-slate-500 text-[11px]">Implied (~30d)</p>
                  <p className={`text-xl font-semibold ${headline > 1.5 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>{headline.toFixed(2)}</p>
                </div>
                <span className="text-slate-400 dark:text-slate-600 text-sm">vs</span>
                <div>
                  <p className="text-slate-500 text-[11px]">Trailing 12M</p>
                  <p className={`text-xl font-semibold ${trailing > 1.5 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>{trailing.toFixed(2)}</p>
                </div>
                <span className={`ml-auto inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold ${
                  Math.abs(delta) < 0.1
                    ? 'bg-slate-200/60 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300'
                    : delta < 0
                      ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300'
                      : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                }`}>
                  {delta >= 0 ? '+' : ''}{delta.toFixed(2)}
                </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-[11px] mt-2">{betaComparisonText(headline, trailing)}</p>
            </div>
          )}

          {/* Term structure by expiration */}
          <p className="text-slate-500 text-[11px] mb-1.5">By expiration</p>
          <div className="grid grid-cols-3 gap-px bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
            {points.map((p) => (
              <div key={p.horizon} className="bg-white dark:bg-slate-800 p-3 text-center">
                <p className="text-slate-500 text-[11px] mb-1">{HORIZON_LABELS[p.horizon] ?? p.horizon}</p>
                <p className={`text-base font-semibold ${(p.impliedBeta ?? 0) > 1.5 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-white'}`}>
                  {p.impliedBeta!.toFixed(2)}
                </p>
                <p className="text-slate-400 dark:text-slate-600 text-[10px] mt-0.5">
                  IV {p.stockImpliedVolPct ?? '—'}% / {p.marketImpliedVolPct ?? '—'}%
                </p>
              </div>
            ))}
          </div>
          <p className="text-slate-400 dark:text-slate-600 text-[11px] mt-2">
            β = ρ × (σ<sub>stock</sub> / σ<sub>{data?.market ?? 'SPY'}</sub>), ATM straddle IV
            {data?.correlation != null && <> · ρ = {data.correlation.toFixed(2)} ({data.correlationWindow})</>}
          </p>
        </>
      )}
    </div>
  )
}

function betaComparisonText(implied: number, trailing: number): string {
  const delta = implied - trailing
  const pct = trailing !== 0 ? Math.abs(delta / trailing) * 100 : 0
  if (Math.abs(delta) < 0.1) {
    return 'Options price systematic risk roughly in line with the trailing 12M regression.'
  }
  const dir = delta < 0 ? 'less' : 'more'
  return `Options imply ~${Math.round(pct)}% ${dir} systematic risk than the trailing 12M regression.`
}
